import { redsStarterItems } from '@/config/reds';
import { addDays, weekdayOf } from '@/domain/businessDate';
import { EMPTY_MICROS, EMPTY_OPENINGS, type BusinessDate, type CountEntry, type Item, type Movement, type Night, type Person, type PlanEvent, type SectionId, type Settings } from '@/domain/types';
import type { LastCallDB } from './db';
import { blankItem } from './items';
import { blankNight, countId } from './nights';
import { DEFAULT_SETTINGS } from './settings';
import { newMeta, nowIso } from './write';

/**
 * Practice mode: Red's real drink lists with MADE-UP numbers (nine weeks of nights), so staff
 * can practise and the app can be shown without touching real counts. Every record is marked
 * isSample, and practice mode keeps its own storage, separate from Red's real records.
 */

export const SAMPLE_VERSION = 2;
export const HISTORY_NIGHTS = 63;

/** Small deterministic random numbers, so the practice bar looks the same every time. */
function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const WEEKDAY = [0.9, 0.55, 0.65, 0.8, 1.0, 1.65, 1.55]; // Sun … Sat

const PEOPLE: { id: string; name: string; initials: string; role: Person['role'] }[] = [
  { id: 'usr-morgan', name: 'Morgan (practice owner)', initials: 'MO', role: 'owner' },
  { id: 'usr-sam', name: 'Sam (practice manager)', initials: 'SM', role: 'manager' },
  { id: 'usr-jordan', name: 'Jordan', initials: 'JD', role: 'staff' },
  { id: 'usr-priya', name: 'Priya', initials: 'PK', role: 'staff' },
  { id: 'usr-alex', name: 'Alex', initials: 'AH', role: 'staff' },
];

let seeding: Promise<void> | null = null;

/** Fills an empty practice storage. Safe to call twice at once (React StrictMode). */
export function seedPractice(db: LastCallDB, today: BusinessDate): Promise<void> {
  seeding ??= doSeed(db, today).finally(() => {
    seeding = null;
  });
  return seeding;
}

async function doSeed(db: LastCallDB, today: BusinessDate): Promise<void> {
  if ((await db.items.count()) > 0) return;
  const rand = rng(20261022);
  const now = nowIso();
  const sample = { isSample: true as const };
  const pick = <T,>(xs: T[]) => xs[Math.floor(rand() * xs.length)]!;
  const between = (a: number, b: number) => a + rand() * (b - a);
  const around = (mean: number) => Math.max(0, Math.round(mean + (rand() + rand() + rand() - 1.5) * Math.sqrt(Math.max(mean, 0.5)) * 1.3));

  // Drinks (Red's lists) with practice amounts and prices.
  const items: Item[] = redsStarterItems().map((s) => {
    const base = { ...newMeta(s.blankRow ? 'itm-blank-row' : `itm-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`), ...sample, ...blankItem(s.name, s.kind, { ...s.rows }), colB: s.colB ?? null, caseSize: s.caseSize ?? null, parCases: s.parCases ?? null, ...(s.blankRow ? { blankRow: true } : {}) };
    if (s.kind === 'beer') {
      const can = /seltzer|truly|twisted|mikes|smirnoff|blackfly|lakelife|homebound|306/i.test(s.name);
      return { ...base, caseSize: base.caseSize ?? (can ? 24 : 12), parCases: base.parCases, usualFront: s.rows.front_beer ? pick([8, 12, 16, 16, 24, 24, 30, 32]) : null, unitCost: Math.round(between(1.4, 2.6) * 100) / 100, drinkPrice: pick([6.25, 6.75, 7.25, 7.5, 8]) };
    }
    const premium = /grey goose|patron|hennessy|knob|glenfiddich|bowmore|auchentoshan|1800|grand marnier/i.test(s.name);
    return { ...base, bottleMl: 1140, unitCost: Math.round((premium ? between(55, 85) : between(28, 42)) * 100) / 100, drinkPrice: premium ? pick([8.5, 9.5, 10]) : pick([6, 6.5, 7]), servingOz: 1 };
  });
  // Front-cooler drinks whose amount changes a lot: counted every time.
  for (const it of items) if (/blackfly|corona 0|homebound/i.test(it.name)) it.usualFront = null;
  await db.items.bulkPut(items);

  await db.people.bulkPut(PEOPLE.map((p) => ({ ...newMeta(p.id), ...sample, name: p.name, initials: p.initials, role: p.role, active: true, pinHash: null })));
  const settings: Settings = { ...DEFAULT_SETTINGS, updatedAt: now, version: 1 };
  await db.settings.put(settings);
  const events: PlanEvent[] = [{ ...newMeta('evt-dry-january'), ...sample, name: 'Dry January', startDate: `${Number(today.slice(0, 4)) + 1}-01-01`, endDate: `${Number(today.slice(0, 4)) + 1}-01-31`, categories: [], changePct: -20, note: 'Practice event.', active: true, createdBy: 'usr-sam' }];
  await db.events.bulkPut(events);

  // Usage per night, and where each drink starts.
  const rate = new Map<string, number>();
  const back = new Map<string, number>();
  const front = new Map<string, number>();
  const oz = new Map<string, number>();
  for (const it of items) {
    if (it.blankRow) continue;
    if (it.kind === 'beer') {
      rate.set(it.id, between(1.5, 14));
      back.set(it.id, Math.round(between(24, 96)));
      front.set(it.id, it.usualFront ?? Math.round(between(6, 20)));
    } else {
      rate.set(it.id, /safe liquor/i.test(it.name) ? 0 : between(0.6, 5.5));
      oz.set(it.id, /safe liquor/i.test(it.name) ? 240 : Math.round(between(25, 70) * 20) / 20);
    }
  }
  // Planted so "Trends noticed" has something real to explain.
  const rising = items.find((i) => i.name === 'Coors Seltzers')!;
  const falling = items.find((i) => i.name === 'Jagermeister')!;

  const counts: CountEntry[] = [];
  const moves: Movement[] = [];
  const nights: Night[] = [];
  const staff = PEOPLE.filter((p) => p.role === 'staff');
  let gun1 = 233_485;
  let gun2 = 66_576;
  const first = addDays(today, -HISTORY_NIGHTS);
  const entry = (date: BusinessDate, section: SectionId, itemId: string, value: number, by: string): CountEntry => ({ ...newMeta(countId(date, section, itemId)), ...sample, date, section, itemId, value, countedBy: by, countedAt: now, source: 'sample', recountRequestedAt: null, recountRequestedBy: null, recountNote: '' });
  const move = (date: BusinessDate, kind: Movement['kind'], item: Item, qty: number, by: string, note = ''): Movement => ({ ...newMeta(`mov-${date}-${kind}-${item.id}`), ...sample, date, kind, sheet: item.kind, itemId: item.id, qty, note, by, at: now, voided: false, voidedBy: null, voidedAt: null });

  for (let n = 0; n < HISTORY_NIGHTS; n++) {
    const date = addDays(first, n);
    const w = WEEKDAY[weekdayOf(date)]!;
    const by = pick(staff);
    const lastNight = n === HISTORY_NIGHTS - 1;
    let beerSold = 0;
    let liquorBackBar = 0;
    for (const it of items) {
      if (it.blankRow) continue;
      let r = rate.get(it.id)! * w;
      const recent = n >= HISTORY_NIGHTS - 16;
      if (it.id === rising.id && recent) r *= 1.7;
      if (it.id === falling.id && recent) r *= 0.45;
      if (it.kind === 'beer') {
        const sold = around(r);
        let b = back.get(it.id)!;
        let f = front.get(it.id)!;
        // Delivery when the back runs low.
        if (b < 30) {
          const cases = Math.max(2, Math.round((it.parCases ?? 3) * 0.8));
          const qty = cases * (it.caseSize ?? 24);
          b += qty;
          moves.push(move(date, 'added', it, qty, by.id, 'Practice delivery'));
        }
        const fromFront = Math.min(f, sold);
        f -= fromFront;
        b -= sold - fromFront;
        // Porters fill the front back up from the back at close.
        const target = it.usualFront ?? Math.round(between(6, 20));
        const refill = Math.max(0, Math.min(b, target - f));
        f += refill;
        b -= refill;
        // One broken bottle now and then.
        if (rand() < 0.015 && b > 0) {
          b -= 1;
          moves.push(move(date, 'promo_spill', it, 1, by.id, 'Dropped a bottle'));
        }
        b = Math.max(0, b);
        back.set(it.id, b);
        front.set(it.id, f);
        beerSold += sold;
        if (it.rows.back_beer != null) counts.push(entry(date, 'back_beer', it.id, b, by.id));
        if (it.rows.front_beer != null) counts.push(entry(date, 'front_beer', it.id, f, by.id));
      } else {
        let level = oz.get(it.id)!;
        if (level < 15 && !/safe liquor/i.test(it.name)) {
          const add = pick([40, 40, 26]);
          level += add;
          moves.push(move(date, 'added', it, add, by.id, 'New bottle'));
        }
        const used = Math.min(level, Math.round(Math.max(0, r + (rand() - 0.5) * r) * 20) / 20);
        level = Math.round((level - used) * 100) / 100;
        oz.set(it.id, level);
        liquorBackBar += used;
        const section: SectionId = it.rows.liquor != null ? 'liquor' : 'liqueur';
        counts.push(entry(date, section, it.id, level, by.id));
      }
    }
    const g1 = Math.round(between(40, 95) * w);
    const g2 = Math.round(between(12, 40) * w);
    gun1 += g1;
    gun2 += g2;
    // Micros rang up roughly what went out, give or take a few.
    const beerMicros = Math.max(0, beerSold + Math.round((rand() - 0.5) * 6));
    const btl5 = Math.floor(beerMicros * 0.08 / 5);
    const btl2 = Math.floor(beerMicros * 0.06 / 2);
    const cooler = Math.round(beerMicros * 0.05);
    const staffBeer = rand() < 0.5 ? 1 : 0;
    const btl1 = Math.max(0, beerMicros - btl5 * 5 - btl2 * 2 - cooler - staffBeer);
    const ozTotal = Math.round(g1 + g2 + liquorBackBar + (rand() - 0.5) * 8);
    const oz2 = Math.floor(ozTotal * 0.25 / 2);
    const oz3 = Math.floor(ozTotal * 0.04 / 3);
    const staffLiquor = rand() < 0.3 ? 1 : 0;
    const oz1 = Math.max(0, ozTotal - oz2 * 2 - oz3 * 3 - staffLiquor);
    const night: Night = {
      ...blankNight(date),
      ...sample,
      status: lastNight ? 'finished' : 'approved',
      areasDone: { back_beer: { by: by.id, at: now }, front_beer: { by: by.id, at: now }, liquor: { by: by.id, at: now }, guns: { by: by.id, at: now } },
      gun1,
      gun2,
      micros: lastNight ? { ...EMPTY_MICROS } : { ...EMPTY_MICROS, btl1, btl2, btl5, btlCooler: cooler, staffBeer, oz1, oz2, oz3, oz4: 0, staffLiquor },
      openings: n === 0 ? { beer: null, liquor: null, gun1: gun1 - g1, gun2: gun2 - g2 } : { ...EMPTY_OPENINGS },
      countInitials: by.initials,
      finishedBy: by.id,
      finishedAt: now,
      approvedBy: lastNight ? null : 'usr-sam',
      approvedAt: lastNight ? null : now,
    };
    nights.push(night);
  }
  // First night: the openings a manager would type (the night before isn't in the app).
  const firstBeer = counts.filter((c) => c.date === first && (c.section === 'back_beer' || c.section === 'front_beer')).reduce((s, c) => s + (c.value ?? 0), 0);
  const firstLiquor = counts.filter((c) => c.date === first && (c.section === 'liquor' || c.section === 'liqueur')).reduce((s, c) => s + (c.value ?? 0), 0);
  nights[0]!.openings = { ...nights[0]!.openings, beer: firstBeer + 140, liquor: Math.round(firstLiquor + 60) };

  // Last night: a delivery nobody wrote down (Bud Light went up) and a liquor count that's off.
  const lastDate = addDays(today, -1);
  const bud = items.find((i) => i.name === 'Bud Light')!;
  const crown = items.find((i) => i.name === 'Crown Royal')!;
  for (const c of counts) {
    if (c.date !== lastDate) continue;
    if (c.itemId === bud.id && c.section === 'back_beer') c.value = (c.value ?? 0) + 48;
    if (c.itemId === crown.id) c.value = Math.max(0, Math.round(((c.value ?? 0) - 22) * 100) / 100);
  }
  await db.counts.bulkPut(counts);
  await db.moves.bulkPut(moves);
  await db.nights.bulkPut(nights);
  await db.meta.put({ key: 'sampleVersion', value: SAMPLE_VERSION });
}
